import moment from 'moment';
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Row, Modal, ModalBody } from 'reactstrap';

import { useDispatch, useSelector } from 'react-redux';

import {
  getTimingStatistics,
  getProfitChart,
  getSessionsStatistics,
  getCategoriesStatistics,
  getCoursesStatistics,
  getArticlesTop,
  getAvgProfitStatistics,
  startDashboardDataSync
} from 'store/actions/dashboardActions';

import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

import { Line, PolarArea } from 'react-chartjs-2';

import { SegmentedControl, SegmentedControlOption } from '@leafygreen-ui/segmented-control';

import PageTitle from 'components/PageTitle';
import DatePicker from 'react-datepicker';

import Select from 'react-select';

import 'react-datepicker/dist/react-datepicker.css';
import './dashboard.scss';

//-------------------------------
// Icons
//-------------------------------
import icArrow from '../../../assets/img/icons/ic_arrow.png';

//-------------------------------

import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend);

// Register German locale for react-datepicker to avoid "locale object not found" warnings
registerLocale('de-DE', de);

const Dashboard = () => {
  // Stato per tracciare se siamo su desktop/tablet
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 992 : false
  );

  useEffect(() => {
    function handleResize() {
      setIsDesktop(window.innerWidth >= 992); // 992px = breakpoint lg di Bootstrap
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const history = useHistory();
  // Manages the filter that specifies the selected days
  // to be applied to the request with the already existing dates
  const [filterDay, setFilterDay] = useState([]);

  // Manages the visibility of the datePicker
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Manages the visibility of the datePicker for comparison filters
  const [showDatePickerCompare, setShowDatePickerCompare] = useState(false);

  // Manages the selection of the date range chosen from
  // the datePicker
  const [dateRange, setDateRange] = useState([null, null]);

  // Manages the selected start and end date
  const [startDate, endDate] = dateRange;

  // Manages the selection of the date range used for the comparison picker
  const [compareDateRange, setCompareDateRange] = useState([null, null]);

  // Manages the selected start and end date for comparison
  const [compareStartDate, compareEndDate] = compareDateRange;

  // Manages the type of filter
  const [filterType, setFilterType] = useState({ value: 'day', label: 'TAG' }); // TAG, WOCHE, MONAT, JAHR, AUSWAHL

  // Search term for filtering articles
  // Manages the selected date to apply to the filter
  const [filterDate, setFilterDate] = useState(new Date());

  // This date is only used
  // when the custom filter (AUSWAHL) is selected
  //
  // The start date is always [filterDate]
  const [filterEndDate, setFilterEndDate] = useState(null);

  // Helper to safely render numeric values coming from APIs or stores.
  // Ensures we never return NaN to React children (returns fallback instead).
  const safeNum = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  // Manages the type of income to show [gross, net]
  const [incomeType, setIncomeType] = useState('gross');

  // Manages the type of category to show [euro, quantity]
  const [categoryType, setCategoryType] = useState('euro');

  // Index to control which chart is visible in the ARTIKELN card (0 = PolarArea, 1 = Doughnut)
  const [chartIndex, setChartIndex] = useState(0);

  // Search term for filtering articles
  const [searchTerm, setSearchTerm] = useState('');

  // State to control the sync modal visibility
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  // Debug message visible to the user when clicking the sync button
  const [syncDebugMessage, setSyncDebugMessage] = useState('');


  // Debounced search term to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Handle search term changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Retrieve statistics from the reducer
  const { categories, sessions, profit, profitChart, timing, articles, courses } = useSelector(
    (state) => state.dashboard
  );

  // List of filter types
  const filterPeriodTypes = [
    { value: 'custom', label: 'AUSWAHL' },
    { value: 'day', label: 'TAG' },
    { value: 'week', label: 'WOCHE' },
    { value: 'month', label: 'MONAT' },
    { value: 'year', label: 'JAHR' }
  ];

  // List of days to integrate into the filter
  const filterDaysTypes = [
    { value: 'monday', label: 'MO' },
    { value: 'tuesday', label: 'DI' },
    { value: 'wednesday', label: 'MI' },
    { value: 'thursday', label: 'DO' },
    { value: 'friday', label: 'FR' },
    { value: 'saturday', label: 'SA' },
    { value: 'sunday', label: 'SO' }
  ];

  //
  const inactiveArticles = 0;

  //
  const avgProPersonPercentage = 0;

  //
  let dispatch = useDispatch();

  const options = useMemo(() => {
    const base = [{ value: filterType.value, label: filterType.label }];
    // only add the explicit 'custom' option if it's not already the active filter type
    if (filterType.value !== 'custom') base.push({ value: 'custom', label: 'AUSWAHL' });
    return base;
  }, [filterType]);

  // State for the compare select - initialize to the current filterType value so it's always in sync
  const [selectedCompare, setSelectedCompare] = useState(filterType.value);

  // Build polar chart data from `categories` coming from the Redux store.
  // Each category is expected to have: { category, totalArticlesInCategory, totalCategoryValue }
  const polarDataCategories = useMemo(() => {
    if (!categories || categories.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 1
          }
        ]
      };
    }

    // Only take the first 4 categories for the polar chart
    const topCategories = categories.slice(0, 4);

    // Use category names as labels and choose data points based on categoryType
    const labels = topCategories.map((c) => {
      return c.category.length > 13 ? `${c.category.substr(0, 12)}.` : c.category;
    });

    const dataValues = topCategories.map((c) => {
      if (categoryType === 'quantity') {
        return typeof c.totalArticlesInCategory === 'number'
          ? c.totalArticlesInCategory
          : Number(c.totalArticlesInCategory) || 0;
      }

      return typeof c.totalCategoryValue === 'number'
        ? c.totalCategoryValue
        : Number(c.totalCategoryValue) || 0;
    });

    // Generate background colors dynamically (semi-transparent)
    const baseColors = [
      '255, 99, 132',
      '54, 162, 235',
      '255, 206, 86',
      '75, 192, 192',
      '153, 102, 255',
      '255, 159, 64',
      '99, 255, 132',
      '162, 54, 235',
      '206, 255, 86',
      '192, 75, 192'
    ];

    const backgroundColor = labels.map((_, idx) => {
      const c = baseColors[idx % baseColors.length];
      return `rgba(${c}, 0.5)`;
    });

    return {
      labels,
      datasets: [
        {
          label: 'KATEGORIEN',
          data: dataValues,
          backgroundColor,
          borderWidth: 1
        }
      ]
    };
  }, [categories, categoryType]);

  const polarDataCourses = useMemo(() => {
    if (!courses || (Array.isArray(courses) && courses.length === 0)) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 1
          }
        ]
      };
    }
    // Courses can be either an array (most common) or an object with .course array
    const courseList = Array.isArray(courses) ? courses : courses?.course || [];
    const topCourses = courseList.slice(0, 4);

    // Use course name as label and choose data points based on categoryType
    const labels = topCourses.map((c) => {
      const label = c?.course ?? String(c ?? '');
      return label && label.length > 13 ? `${label.substr(0, 12)}.` : label;
    });

    const dataValues = topCourses.map((c) => {
      if (categoryType === 'quantity') {
        return typeof c.totalArticlesInCourse === 'number'
          ? c.totalArticlesInCourse
          : Number(c.totalArticlesInCourse) || 0;
      }

      return typeof c.totalCourseValue === 'number'
        ? c.totalCourseValue
        : Number(c.totalCourseValue) || 0;
    });

    // Generate background colors dynamically (semi-transparent)
    const baseColors = [
      '255, 99, 132',
      '54, 162, 235',
      '255, 206, 86',
      '75, 192, 192',
      '153, 102, 255',
      '255, 159, 64',
      '99, 255, 132',
      '162, 54, 235',
      '206, 255, 86',
      '192, 75, 192'
    ];

    const backgroundColor = labels?.map((_, idx) => {
      const c = baseColors[idx % baseColors.length];
      return `rgba(${c}, 0.5)`;
    });

    return {
      labels,
      datasets: [
        {
          label: 'COURSES',
          data: dataValues,
          backgroundColor,
          borderWidth: 1
        }
      ]
    };
  }, [courses, categoryType]);

  const optionsPolarArea = {
    scales: {
      r: {
        angleLines: { color: '#ccc' },
        grid: { color: '#999' },
        ticks: {
          backdropColor: 'rgba(0, 0, 0, 0)',
          color: '#fff',
          showLabelBackdrop: true
        }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: '#fff',
          font: { size: 10 },
          boxWidth: 10,
          boxHeight: 10
        }
      }
    }
  };

  const optionsLine = useMemo(
    () => ({
      responsive: true,
      scales: {
        x: {
          display: true,
          title: {
            display: true
          },
          ticks: {
            callback: function (value) {
              try {
                const chart = this.chart || this?.axis?.chart;
                const labels = chart?.data?.labels || [];
                const label = labels[value] ?? labels[Math.floor(value)] ?? '';
                if (filterType.value === 'day') {
                  return typeof label === 'string' && label.includes(':')
                    ? label.split(':')[0]
                    : label;
                }

                if (filterType.value === 'week') {
                  return typeof label === 'string' && label.includes(':')
                    ? label.split(':')[0]
                    : label;
                }

                return label;
              } catch (e) {
                const labelFallback = this.getLabelForValue ? this.getLabelForValue(value) : '';
                return labelFallback;
              }
            }
          }
        },
        y: {
          display: true,
          title: {
            display: false
          },
          suggestedMin: -10,
          suggestedMax: 200
        }
      },
      interaction: {
        intersect: false
      },
      ...(isDesktop ? { maintainAspectRatio: false } : {})
    }),
    [filterType, isDesktop]
  );

  // Build line chart data for profitChart coming from Redux store.
  // Uses useMemo to avoid recalculating on every render unless profitChart changes.
  const lineData = useMemo(() => {
    // Ensure labels are strings and avoid Date objects that Chart.js may re-parse
    const labels = (profitChart?.report || []).map((v) => {
      // If label looks like an ISO date, keep it as a string in UTC form using moment
      if (typeof v.label === 'string') return v.label;

      // If label is a Date instance, format it consistently
      if (v.label instanceof Date) return moment.utc(v.label).format('YYYY-MM-DDTHH:mm');

      // Fallback to string conversion
      return String(v.label);
    });

    return {
      labels,
      datasets: [
        {
          label: 'INPUT',
          data: profitChart?.report?.map((value) => value.value) ?? [],
          borderColor: '#459d98',
          backdropColor: '#459d98'
        },
        {
          label: 'VERGLEICH',
          data: profitChart?.compare?.map((value) => value.value) ?? [],
          borderColor: '#2d555366'
        }
      ]
    };
  }, [profitChart]);

  //-------------------------------
  useEffect(() => {
    loadStats();
  }, [
    filterType,
    filterDate,
    filterDay,
    filterEndDate,
    selectedCompare,
    compareStartDate,
    compareEndDate
  ]);

  //-------------------------------
  useEffect(() => {
    // Prepare the parameters for the timing statistics API call
    const params = {
      scale: filterType.value,
      ...(debouncedSearch ? { q: debouncedSearch } : {})
    };

    // Da implementare nelle api
    if (filterType.value === 'custom') {
      params.startDate = moment(filterDate).format('YYYY-MM-DD');
      params.endDate = moment(filterEndDate).format('YYYY-MM-DD');
    }

    if (filterType.value === 'year') {
      params.year = filterDate.getFullYear();
    }

    if (filterType.value === 'month') {
      params.month = `${filterDate.getFullYear()}-${filterDate.getMonth() + 1}`; // I mesi in JavaScript sono 0-indexed
    }

    if (filterType.value === 'week') {
      params.year = filterDate.getFullYear();

      params.week = moment(filterDate).isoWeek() - 1;
    }

    if (filterType.value === 'day') {
      params.date = moment(filterDate).format('YYYY-MM-DD');
    }

    if (filterDay.length > 0) {
      params.weekdays = filterDay.map((d) => d);
    }


    console.log("selectedCompare:");
    console.log(selectedCompare);
    
    console.log("compareEndDate:");
    console.log(compareEndDate);

    // Include comparison parameters if provided by the compare select
    if (selectedCompare) {
      params.compareTo = selectedCompare;

      if (selectedCompare === 'custom') {
        if (!compareStartDate || !compareEndDate) {
          return;
        }
        params.compareStartDate = moment(compareStartDate).format('YYYY-MM-DD');
        params.compareEndDate = moment(compareEndDate).format('YYYY-MM-DD');
      }
    }

    dispatch(getArticlesTop(params));
  }, [debouncedSearch]);

  //-------------------------------
  function loadStats() {
    // Prepare the parameters for the timing statistics API call
    const params = {
      scale: filterType.value
    };

    // Da implementare nelle api
    if (filterType.value === 'custom') {
      params.startDate = moment(filterDate).format('YYYY-MM-DD');
      params.endDate = moment(filterEndDate).format('YYYY-MM-DD');
    }

    if (filterType.value === 'year') {
      params.year = filterDate.getFullYear();
    }

    if (filterType.value === 'month') {
      params.month = `${filterDate.getFullYear()}-${filterDate.getMonth() + 1}`; // I mesi in JavaScript sono 0-indexed
    }

    if (filterType.value === 'week') {
      params.year = filterDate.getFullYear();

      params.week = moment(filterDate).isoWeek() - 1;
    }

    if (filterType.value === 'day') {
      params.date = moment(filterDate).format('YYYY-MM-DD');
    }

    if (filterDay.length > 0) {
      params.weekdays = filterDay.map((d) => d);
    }

    // Include comparison parameters if provided by the compare select
    if (selectedCompare) {
      params.compareTo = selectedCompare;

      if (selectedCompare === 'custom') {
        if (!compareStartDate || !compareEndDate) {
          return;
        }

        params.compareStartDate = moment(compareStartDate).format('YYYY-MM-DD');
        params.compareEndDate = moment(compareEndDate).format('YYYY-MM-DD');
        
      }
    }

    dispatch(getTimingStatistics(params));
    dispatch(getSessionsStatistics(params));
    dispatch(getCategoriesStatistics(params));
    dispatch(getAvgProfitStatistics(params));
    dispatch(getProfitChart(params));
    dispatch(getArticlesTop(params));
    dispatch(getCoursesStatistics(params));
  }

  //-------------------------------
  // Function to get the text to display based on the filter
  function getFilterLabel(date, type) {
    if (!date) return '';

    switch (type.value) {
      case 'day':
        return date.toLocaleDateString('de-DE', {
          weekday: 'long',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      case 'week': {
        // Calculate start and end of the week (Monday-Sunday)
        const curr = new Date(date);
        const first = new Date(curr);

        first.setDate(curr.getDate() - ((curr.getDay() + 6) % 7)); // Lunedì

        const last = new Date(first);
        last.setDate(first.getDate() + 6); // Domenica

        return `${first.toLocaleDateString('de-DE')} - ${last.toLocaleDateString('de-DE')}`;
      }
      case 'month':
        return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

      case 'year':
        return date.getFullYear();

      case 'custom':
        return `${date.toLocaleDateString('de-DE')} - ${filterEndDate.toLocaleDateString('de-DE')}`;

      default:
        return '';
    }
  }

  //-------------------------------
  // Function to change the date with the arrows
  function handleArrowClick(direction) {
    let newDate = new Date(filterDate);

    switch (filterType.value) {
      case 'day':
        newDate.setDate(newDate.getDate() + direction);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7 * direction);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + direction);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + direction);
        break;

      case 'custom': {
        // Initialize the data as a string
        let endDate = new Date(filterEndDate);

        // Convert the dates to moment type
        let momentNewDate = moment(newDate);
        let momentEndDate = moment(endDate);

        // Calculate the difference between the two dates
        let difference = momentNewDate.diff(momentEndDate);

        // Retrieve the duration
        const diffDuration = moment.duration(difference);

        // Add the difference to the start date
        newDate.setDate(newDate.getDate() + diffDuration.days() * direction);

        // Add the diference to the end date
        endDate.setDate(endDate.getDate() + diffDuration.days() * direction);

        // Set the filter end date
        setFilterEndDate(endDate);

        break;
      }

      default:
        break;
    }

    setFilterDate(newDate);
  }

  //-------------------------------
  // Handles click on the filter type cards
  function handleFilterTypeChange(type) {
    // Check if the selected filter is the custom one
    // in that case, the datePicker must be shown
    if (type.value == 'custom') {
      setShowDatePicker(true);
      // set the compare select to custom as well
      setSelectedCompare(type.value);
    } else {
      setDateRange([null, null]); // range reset
      setFilterType(type);
      setFilterDate(new Date()); // resetta la date on change
      // keep compare in sync with the top filter
      setSelectedCompare(type.value);
    }
  }

  //-------------------------------
  // Handles click on the day cards to
  // add them to the applied filter
  function handleFilterDaysChange(type) {
    if (filterDay.includes(type.value)) {
      // if already selected → remove it
      setFilterDay(filterDay.filter((d) => d !== type.value));
    } else {
      // otherwise add it
      setFilterDay([...filterDay, type.value]);
    }
  }

  //-------------------------------
  // Start dashboard data sync and show loading modal
  async function handleStartSync() {
    try {
      
      setSyncDebugMessage('Synchronisierung wird gestartet...');

      setSyncModalOpen(true);


      // dispatch the thunk and wait for completion (action now returns response)
      const result = await dispatch(startDashboardDataSync());

      if (result && typeof result === 'object') {
        // Response structure handled:
        // { success: true, message: "No orders found to process", processedCount: 0, errorCount: 0, processingTime: 23 }
        const { success, message, processedCount, errorCount, processingTime } = result;

        if (success) {
          let userMsg = message || 'Synchronisierung abgeschlossen';
          // add summary details if available
          if (processedCount !== undefined || errorCount !== undefined || processingTime !== undefined) {
            userMsg += ` — Verarbeitet: ${processedCount ?? 0}, Fehler: ${errorCount ?? 0}, Zeit: ${processingTime ?? 0}s`;
          }
          setSyncDebugMessage(userMsg);
        } else {
          setSyncDebugMessage(message || 'Synchronisierung fehlgeschlagen');
        }
      } else {
        setSyncDebugMessage('Ungültige Antwort vom Server');
      }

      // auto clear debug message after 4s
      setTimeout(() => setSyncDebugMessage(''), 4000);
    } catch (err) {
      setSyncDebugMessage('Fehler während der Synchronisierung');
      setTimeout(() => setSyncDebugMessage(''), 4000);
    } finally {
      setSyncModalOpen(false);
    }
  }

  //-------------------------------
  function onSelectFilterCompare(type) {
    // Check if the selected filter is the custom one
    // in that case, the datePicker must be shown
    if (type === 'custom') {
      setShowDatePickerCompare(true);
    }
  }

  //-------------------------------
  function secondsToMinutes(sec) {
    if (!sec && sec !== 0) return 0;
    return (sec / 60).toFixed(2);
  }

  // Apply income type to a monetary value: if incomeType is 'net', reduce by 19% (remove VAT)
  function applyIncomeType(value) {
    // If value is null/undefined/NaN, return 0
    const numeric = Number(value);
    if (!numeric && numeric !== 0) return 0;

    if (incomeType === 'net') {
      // Remove 19% VAT
      return numeric / (1.19);
    }

    return numeric;
  }

  function formatCurrency(value) {
    const val = applyIncomeType(value);
    // format with 2 decimals and comma as decimal separator? Keep default en-GB/d e-DE? use de-DE
    return `${val.toFixed(2)} `;
  }

  //-------------------------------
  // UI
  return (
    <>
      <Row className="py-3 px-1">
        {/* Date-picker for select custom days on a filter */}
        {syncDebugMessage ? (
          <Col xs="12" className="mb-2">
            <div className="alert alert-info mb-0 py-1" role="alert">
              {syncDebugMessage}
            </div>
          </Col>
        ) : null}
        <Modal isOpen={showDatePicker} centered toggle={() => setShowDatePicker(false)}>
          <ModalBody className="d-flex flex-column align-items-center">
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                setDateRange(update);
                if (update[0] && update[1]) {
                  setShowDatePicker(false);
                  setFilterDate(update[0]);
                  setFilterEndDate(update[1]);
                  setFilterType({ value: 'custom', label: 'AUSWAHL' });
                }
              }}
              inline
                locale={de}
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
            />
            <button
              className="btn btn-sm btn-secondary mt-2"
              onClick={() => setShowDatePicker(false)}>
              Schließe
            </button>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={showDatePickerCompare}
          centered
          toggle={() => setShowDatePickerCompare(false)}>
          <ModalBody className="d-flex flex-column align-items-center">
            <DatePicker
              selectsRange
              startDate={compareStartDate}
              endDate={compareEndDate}
              onChange={(update) => {
                setCompareDateRange(update);
                if (update[0] && update[1]) {
                  // keep the compare range and close the picker
                  setShowDatePickerCompare(false);
                }
              }}
              inline
              locale={de}
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
            />
            <button
              className="btn btn-sm btn-secondary mt-2"
              onClick={() => setShowDatePickerCompare(false)}>
              Close
            </button>
          </ModalBody>
        </Modal>

        <Col lg="6" sm="12" md="6" xs="12">
          <div className="py-2">
            <PageTitle icon={'/img/sidebar/ic_dashboard.png'} title="Dashboard" />
          </div>
        </Col>
        <Col lg="12" sm="12" md="12" className="mb-2">
          <Row className="d-flex justify-content-end">
            <button
              type="button"
              //className="btn btn-sm d-flex align-items-center py-2"
              className={`p-1 d-flex align-items-center card filter-card filter-card--wide`}
              onClick={handleStartSync}
              onMouseDown={handleStartSync}
              onTouchStart={handleStartSync}
              aria-label="sync-dashboard"
              style={{
                background: syncModalOpen ? '#459d98' : '#A0B2B9',
                borderColor: syncModalOpen ? '#459d98' : '#A0B2B9',
                color: syncModalOpen ? '#fff' :  '#000',
              }}
            >
              
              <div className="p-1 px-2 d-flex align-items-center">
                <i className="fas fa-sync-alt mr-2" />
                <span>Aktualisieren</span>
              </div>
     
            </button>
          </Row>
        </Col>
        <Col lg="12" sm="12" md="12">
          <Row className="mb-2 align-items-center">
            {/* Period applied to the filter */}
            <Col xs="12" sm="12" md="3" lg="3" className="m-0 px-1 flex-row align-items-center">
              <div className="card flex-row justify-content-between align-items-center py-2 px-3 idle-card">
                <i
                  className="fas fa-chevron-left"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleArrowClick(-1)}
                />
                <div>
                  <b>{getFilterLabel(filterDate, filterType)}</b>
                </div>
                <i
                  className="fas fa-chevron-right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleArrowClick(1)}
                />
              </div>
            </Col>

            {/* List containing the filter types */}
            <div className="scroll-cards-wrapper mt-2">
              {filterPeriodTypes.map((type) => {
                const isActive = filterType.value === type.value;
                const isCustom = type.value === 'custom';

                return (
                  <div
                    key={type.value}
                    className={` p-2 mx-1 text-center ${isActive ? 'active' : ''} ${
                      isCustom ? 'small-card' : 'card filter-card'
                    }`}
                    style={{
                      cursor: 'pointer',
                      background: isActive ? '#459d98' : '#A0B2B9'
                    }}
                    onClick={() => handleFilterTypeChange(type)}>
                    {!isCustom ? (
                      <span
                        className={
                          isActive ? 'text-white font-weight-bold' : 'text-body font-weight-normal'
                        }>
                        {type.label}
                      </span>
                    ) : (
                      <i
                        className={
                          isActive
                            ? 'text-white far fa-calendar-alt'
                            : 'text-body far fa-calendar-alt'
                        }></i>
                    )}
                  </div>
                );
              })}
            </div>

            {/* List of days applicable to the filter */}
            {filterType.value !== 'day' ? (
              <div className="scroll-cards-wrapper mt-2">
                {filterDaysTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`card py-1 mx-1 text-center days-card ${
                      filterDay.includes(type.value) ? 'active' : ''
                    }`}
                    style={{
                      cursor: 'pointer',
                      background: filterDay.includes(type.value) ? '#459d98' : '#A0B2B9'
                    }}
                    onClick={() => handleFilterDaysChange(type)}>
                    <span
                      className={
                        filterDay.includes(type.value)
                          ? 'text-white font-weight-bold'
                          : 'text-body font-weight-normal'
                      }>
                      {type.label}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div></div>
            )}
          </Row>

          <div
            style={
              isDesktop
                ? { height: 200, width: '100%', marginBottom: 24 }
                : {
                    width: '100%',
                    marginBottom: 16,
                    minHeight: 180,
                    maxWidth: '100vw',
                    overflowX: 'auto'
                  }
            }>
            <Line
              datasetIdKey="id"
              data={lineData}
              options={optionsLine}
              width={undefined}
              height={undefined}
              style={
                isDesktop
                  ? { height: '100%', width: '100%' }
                  : { width: '100%', minHeight: 180, maxWidth: '100vw' }
              }
            />
          </div>

          <Row className="d-flex justify-content-between align-items-center mx-2">
            <SegmentedControl
              name="incomeType"
              size={'default'}
              darkMode={false}
              followFocus={true}
              defaultValue="gross"
              className="custom-segmented"
              onChange={(value) => setIncomeType(value)}>
              <SegmentedControlOption value="gross">Brutto</SegmentedControlOption>
              <SegmentedControlOption value="net">Netto</SegmentedControlOption>
            </SegmentedControl>

            <Row className="d-flex align-items-center mt-2">
              <div
                className="label-text mx-2"
                style={isDesktop ? { fontSize: 12 } : { fontSize: 8 }}>
                VERGLEICH
              </div>
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={options.find((o) => o.value === selectedCompare) || options[0]}
                name="color"
                options={options}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: 20,
                    padding: 0,
                    minHeight: 36,
                    height: 36
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: 36,
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center'
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: 10
                  }),
                  menuList: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: 0,
                    fontSize: 12
                  })
                }}
                onChange={(e) => {
                  setSelectedCompare(e.value);
                  // If custom, open the compare datepicker. Otherwise reset any previously selected compare range.
                  if (e.value === 'custom') {
                    setShowDatePickerCompare(true);
                  } else {
                    setCompareDateRange([null, null]);
                  }
                  onSelectFilterCompare(e.value);
                }}
              />
            </Row>
          </Row>

          <div className="divider-horizontal"></div>

          <Row xs="12" sm="12" md="6" lg="6" className="d-flex justify-content-between mx-2">
            <div style={isDesktop ? { textAlign: 'left' } : { textAlign: 'left' }}>
              <div
                className="label-text"
                style={
                  isDesktop ? { fontSize: 12, marginBottom: 4 } : { fontSize: 10, marginBottom: 4 }
                }>
                {formatCurrency(sessions?.compare?.totalValue ?? 0)}€
              </div>
              <div
                className={`big-text-bold ${
                  (sessions?.percentageVariation?.totalValue ?? 0) >= 0
                    ? 'text-success'
                    : 'text-danger'
                }`}>
                {sessions?.percentageVariation?.totalValue ?? 0}%
              </div>
            </div>
            <div className={`big-text-bold`}>{formatCurrency(sessions?.totalValue ?? 0)}€</div>
          </Row>

          {/* --- Nuovo layout responsive per le card --- */}
          {isDesktop ? (
            <Row className="mt-3">
              <Col lg="6" md="6" sm="12" xs="12">
                {/* Card ARTIKELN */}
                <div className="card session-card">
                  <div className="d-flex flex-column mx-3 mt-2">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="title-text">
                          {chartIndex === 1 ? 'COURSES' : 'KATEGORIEN'}
                        </div>
                        <img
                          src={icArrow}
                          alt="arrow"
                          width={25}
                          height={25}
                          style={{ objectFit: 'contain' }}
                          onClick={() => history.push('/store/articles/articles')}
                        />
                      </div>
                    </div>
                  </div>
                  <Col className="card-body">
                    <Row className={`d-flex justify-content-center my-2`}>
                      <SegmentedControl
                        name="categoryType"
                        size={'default'}
                        darkMode={false}
                        followFocus={true}
                        defaultValue="euro"
                        className="custom-segmented"
                        onChange={(value) => setCategoryType(value)}>
                        <SegmentedControlOption value="euro">€</SegmentedControlOption>
                        <SegmentedControlOption value="quantity">Menge</SegmentedControlOption>
                      </SegmentedControl>
                    </Row>
                    <Row className={`d-flex justify-content-center`}>
                      <div
                        style={{
                          position: 'relative',
                          width: isDesktop ? '100%' : '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                        <button
                          aria-label="prev-chart"
                          onClick={() => setChartIndex((prev) => (prev === 0 ? 1 : prev - 1))}
                          style={{
                            position: 'absolute',
                            left: 8,
                            zIndex: 2,
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: 18,
                            cursor: 'pointer'
                          }}>
                          <i className="fas fa-chevron-left" />
                        </button>

                        <div style={{ width: isDesktop ? 420 : 300, maxWidth: '100%' }}>
                          {chartIndex === 0 ? (
                            <PolarArea data={polarDataCategories} options={optionsPolarArea} />
                          ) : (
                            <PolarArea data={polarDataCourses} options={optionsPolarArea} />
                          )}
                        </div>

                        <button
                          aria-label="next-chart"
                          onClick={() => setChartIndex((prev) => (prev === 1 ? 0 : prev + 1))}
                          style={{
                            position: 'absolute',
                            right: 8,
                            zIndex: 2,
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: 18,
                            cursor: 'pointer'
                          }}>
                          <i className="fas fa-chevron-right" />
                        </button>
                      </div>
                    </Row>
                    <div className="divider-horizontal"></div>
                    <Row className={`text-center mb-0`}>
                      <Col className="d-flex flex-column justify-content-center">
                        <div className="title-text">ARTIKELN</div>
                        <div className="position-relative w-50 mx-auto my-4">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Suche"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              backgroundColor: '#fff',
                              border: '1px solid #fff',
                              borderRadius: '20px',
                              height: '38px',
                              paddingLeft: '35px'
                            }}
                          />
                          <i
                            className="fas fa-search"
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#459d98'
                            }}
                          />
                        </div>
                      </Col>

                      {articles?.topSellingArticles.length > 0 ||
                      articles?.topRevenueArticles.length > 0 ? (
                        categoryType === 'quantity' ? (
                          <ul className="list-unstyled mt-2 mx-2 w-100 mb-0">
                            {articles.topSellingArticles.map((article, idx) => (
                              <li
                                key={`topSelling-${idx}`}
                                className="d-flex justify-content-between align-items-center mb-1">
                                <span>
                                  {article.itemName.length > 24
                                    ? `${article.itemName.substr(0, 24)}...`
                                    : article.itemName}{' '}
                                  - {safeNum(article.totalValue)} €
                                </span>
                                <span className="font-weight-bold">{safeNum(article.count)}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <ul className="list-unstyled mt-2 mx-2 w-100 mb-0">
                            {articles.topRevenueArticles.map((article, idx) => (
                              <li
                                key={`topRevenue-${idx}`}
                                className="d-flex justify-content-between align-items-center mb-1">
                                <span>
                                  {article.itemName.length > 24
                                    ? `${article.itemName.substr(0, 24)}...`
                                    : article.itemName}{' '}
                                  - {safeNum(article.count)}
                                </span>
                                <span className="font-weight-bold">
                                  {safeNum(article.totalValue)} €
                                </span>
                              </li>
                            ))}
                          </ul>
                        )
                      ) : (
                        <div className="w-100 text-center">KEINE DATEN VERFÜGBAR</div>
                      )}
                    </Row>
                  </Col>
                  <Row className="card-body d-flex justify-content-center">
                    <div className="title-text">
                      INAKTIVE ARTIKELN: <b className="text-danger">{inactiveArticles}</b>
                    </div>
                  </Row>
                </div>
              </Col>
              <Col lg="6" md="6" sm="12" xs="12">
                {/* Colonna DESTRA: BESTELLUNGEN, GÄSTE, TIMING */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Card BESTELLUNGEN */}
                  <div className="card session-card">
                    <Row className="d-flex align-items-center justify-content-between mx-3 mt-2">
                      <div className="d-flex flex-row align-items-center">
                        <div className="title-text">BESTELLUNGEN - </div>
                        <div className="ml-1 bigger-text-bold">{safeNum(sessions?.totalCount)}</div>
                      </div>
                      <img
                        src={icArrow}
                        alt="arrow"
                        width={25}
                        height={25}
                        style={{ objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => history.push('/store/session/settings')}
                      />
                    </Row>
                    <Row className="card-body d-flex justify-content-center">
                      <Col className="text-center">
                        <div className="title-text">OFFEN </div>
                        <div className="big-text-bold">
                          {safeNum(sessions?.openSessions?.count)}
                        </div>
                        <div className="subtitle-text">
                          {safeNum(sessions?.openSessions?.totalValue)} €
                        </div>
                      </Col>
                      <div className="divider"></div>
                      <Col className="text-center">
                        <div className="title-text">GESCHLOSSENE</div>
                        <div className="big-text-bold">
                          {safeNum(sessions?.closedSessions?.count)}
                        </div>
                        <div className="subtitle-text">
                          {safeNum(sessions?.closedSessions?.totalValue)} €
                        </div>
                      </Col>
                    </Row>
                  </div>
                  {/* Card GÄSTE */}
                  <div className="card session-card">
                    <Row className="d-flex align-items-center mx-3 mt-1">
                      <div className="title-text">GÄSTE - </div>
                      <div className="ml-1 bigger-text-bold">
                        {safeNum(sessions?.totalCount) * 2}
                      </div>
                    </Row>
                    <Row className="card-body mr-2">
                      <Col lg="6" sm="6" md="6" xs="6" className="text-start">
                        <div className="label-text">Ø / GAST</div>
                        <div className="big-text-bold">
                          {formatCurrency(profit?.perPerson?.avgProfitPerPerson ?? 0)}€
                        </div>
                        <div
                          className={`label-text ${
                            avgProPersonPercentage >= 0 ? 'text-success' : 'text-danger'
                          }`}>
                          {avgProPersonPercentage}%
                        </div>
                      </Col>
                      <Col lg="6" sm="6" md="6" xs="6" className="text-center">
                        <Row className="d-flex justify-content-between">
                          <div className="label-text">Ø / TISCH</div>
                          <div className="label-text font-weight-bold">
                            {formatCurrency(profit?.perPerson?.avgProfitPerOrder ?? 0)}€
                          </div>
                        </Row>
                        <Row className="d-flex justify-content-between my-2">
                          <div className="label-text">Ø / GAST-TISCH</div>
                          <div className="label-text font-weight-bold">2</div>
                        </Row>
                        <Row className="d-flex justify-content-between">
                          <div className="label-text">Ø / STD</div>
                          <div className="label-text font-weight-bold">
                            {formatCurrency(profit?.perHours?.value ?? 0)}€
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                  {/* Card TIMING */}
                  <div className="card session-card">
                    <div className="d-flex align-items-center justify-content-between mx-3 mt-3">
                      <div className="title-text ">TIMING</div>
                      <img
                        src={icArrow}
                        alt="arrow"
                        width={25}
                        height={25}
                        style={{ objectFit: 'contain' }}
                        onClick={() => history.push('/store/kitchen/users')}
                      />
                    </div>
                    <Row className="card-body mr-2">
                      <Col lg="5" sm="5" md="5" xs="5">
                        <div className="label-text">SESSION</div>
                        <div className="big-text-bold">
                          {secondsToMinutes(timing?.perOrder?.avgSessionStartToClosed) ?? 0} MIN
                        </div>
                      </Col>
                      <Col lg="7" sm="7" md="7" xs="7">
                        <Col className="px-0 mx-0 mt-3">
                          <Row>
                            <div className="label-text medium-text-bold">PER ORDER</div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">KITCHEN</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByNonBar?.avgPreparationTimePerOrder
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">BAR</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByBar?.avgPreparationTimePerOrder
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                        </Col>

                        <Col className="px-0 mx-0 mt-3">
                          <Row>
                            <div className="label-text medium-text-bold">PER ITEM</div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">KITCHEN</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByNonBar?.avgPreparationTimePerItem
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">BAR</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByBar?.avgPreparationTimePerItem
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                        </Col>

                        <Row className="d-flex justify-content-between mt-3">
                          <div className="label-text">COOKING START</div>
                          <div className="medium-text-bold">
                            {secondsToMinutes(timing?.cookingStart?.avgOrderToCookingStarted)} MIN
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            // --- Layout mobile/tablet: UI invariata ---
            <>
              {/* Card containing session statistics */}
              <Row>
                <Col xs="12" sm="12" md="6" lg="6">
                  <div className="card session-card">
                    <Row className="d-flex align-items-center justify-content-between mx-3 mt-2">
                      <div className="d-flex flex-row align-items-center">
                        <div className="title-text">BESTELLUNGEN - </div>
                        <div className="ml-1 bigger-text-bold">{safeNum(sessions?.totalCount)}</div>
                      </div>
                      <img
                        src={icArrow}
                        alt="arrow"
                        width={25}
                        height={25}
                        style={{ objectFit: 'contain', cursor: 'pointer' }}
                        onClick={() => history.push('/store/session/settings')}
                      />
                    </Row>
                    <Row className="card-body d-flex justify-content-center">
                      <Col className="text-center">
                        <div className="title-text">OFFEN </div>
                        <div className="big-text-bold">
                          {safeNum(sessions?.openSessions?.count)}
                        </div>
                        <div className="subtitle-text">
                          {safeNum(sessions?.openSessions?.totalValue)} €
                        </div>
                      </Col>
                      <div className="divider"></div>
                      <Col className="text-center">
                        <div className="title-text">GESCHLOSSENE</div>
                        <div className="big-text-bold">
                          {safeNum(sessions?.closedSessions?.count)}
                        </div>
                        <div className="subtitle-text">
                          {safeNum(sessions?.closedSessions?.totalValue)} €
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              {/* Card containing article statistics */}
              <Row>
                <Col xs="12" sm="12" md="6" lg="6">
                  <div className="card session-card">
                    <div className="d-flex align-items-center justify-content-between mx-3 mt-2">
                      <div className="title-text">
                        {chartIndex === 1 ? 'COURSES' : 'KATEGORIEN'}
                      </div>
                      <img
                        src={icArrow}
                        alt="arrow"
                        width={25}
                        height={25}
                        style={{ objectFit: 'contain' }}
                        onClick={() => history.push('/store/articles/articles')}
                      />
                    </div>
                    <Col className="card-body">
                      <Row className={`d-flex justify-content-center my-2`}>
                        <SegmentedControl
                          name="categoryType"
                          size={'default'}
                          darkMode={false}
                          followFocus={true}
                          defaultValue="euro"
                          className="custom-segmented"
                          onChange={(value) => setCategoryType(value)}>
                          <SegmentedControlOption value="euro">€</SegmentedControlOption>
                          <SegmentedControlOption value="quantity">Menge</SegmentedControlOption>
                        </SegmentedControl>
                      </Row>
                      <Row className={`d-flex justify-content-center`}>
                        <div
                          style={{
                            position: 'relative',
                            width: isDesktop ? '100%' : '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                          <button
                            aria-label="prev-chart-mobile"
                            onClick={() => setChartIndex((prev) => (prev === 0 ? 1 : prev - 1))}
                            style={{
                              position: 'absolute',
                              left: 8,
                              zIndex: 2,
                              background: 'transparent',
                              border: 'none',
                              color: '#fff',
                              fontSize: 16,
                              cursor: 'pointer'
                            }}>
                            <i className="fas fa-chevron-left" />
                          </button>

                          <div style={{ width: isDesktop ? 420 : 300, maxWidth: '100%' }}>
                            {chartIndex === 0 ? (
                              <PolarArea data={polarDataCategories} options={optionsPolarArea} />
                            ) : (
                              <PolarArea data={polarDataCourses} options={optionsPolarArea} />
                            )}
                          </div>

                          <button
                            aria-label="next-chart-mobile"
                            onClick={() => setChartIndex((prev) => (prev === 1 ? 0 : prev + 1))}
                            style={{
                              position: 'absolute',
                              right: 8,
                              zIndex: 2,
                              background: 'transparent',
                              border: 'none',
                              color: '#fff',
                              fontSize: 16,
                              cursor: 'pointer'
                            }}>
                            <i className="fas fa-chevron-right" />
                          </button>
                        </div>
                      </Row>
                      <div className="divider-horizontal"></div>
                      <Row className={`text-center mb-0`}>
                         <Col className="d-flex flex-column justify-content-center">
                        <div className="title-text">ARTIKELN</div>
                        <div className="position-relative w-50 mx-auto my-4">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Suche"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              backgroundColor: '#fff',
                              border: '1px solid #fff',
                              borderRadius: '20px',
                              height: '38px',
                              paddingLeft: '35px'
                            }}
                          />
                          <i
                            className="fas fa-search"
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#459d98'
                            }}
                          />
                        </div>
                      </Col>

                        {articles?.topSellingArticles.length > 0 ||
                        articles?.topRevenueArticles.length > 0 ? (
                          categoryType === 'quantity' ? (
                            <ul className="list-unstyled mt-2 mx-2 w-100 mb-0">
                              {articles.topSellingArticles.map((article, idx) => (
                                <li
                                  key={`topSelling-${idx}`}
                                  className="d-flex justify-content-between align-items-center mb-1">
                                  <span>
                                    {article.itemName.length > 24
                                      ? `${article.itemName.substr(0, 24)}...`
                                      : article.itemName}{' '}
                                    - {safeNum(article.totalValue)} €
                                  </span>
                                  <span className="font-weight-bold">{safeNum(article.count)}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="list-unstyled mt-2 mx-2 w-100 mb-0">
                              {articles.topRevenueArticles.map((article, idx) => (
                                <li
                                  key={`topRevenue-${idx}`}
                                  className="d-flex justify-content-between align-items-center mb-1">
                                  <span>
                                    {article.itemName.length > 24
                                      ? `${article.itemName.substr(0, 24)}...`
                                      : article.itemName}{' '}
                                    - {safeNum(article.count)}
                                  </span>
                                  <span className="font-weight-bold">
                                    {safeNum(article.totalValue)} €
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )
                        ) : (
                          <div className="w-100 text-center">KEINE DATEN VERFÜGBAR</div>
                        )}
                      </Row>
                    </Col>
                    <Row className="card-body d-flex justify-content-center">
                      <div className="title-text">
                        INAKTIVE ARTIKELN: <b className="text-danger">{inactiveArticles}</b>
                      </div>
                    </Row>
                  </div>
                </Col>
              </Row>
              {/* Card contenente le statistiche delle medie */}
              <Row>
                <Col xs="12" sm="12" md="6" lg="6">
                  <div className="card session-card">
                    <Row className="d-flex align-items-center mx-3 mt-1">
                      <div className="title-text">GÄSTE - </div>
                      <div className="ml-1 bigger-text-bold">
                        {safeNum(sessions?.totalCount) * 2}
                      </div>
                    </Row>
                    <Row className="card-body mr-2">
                      <Col lg="6" sm="6" md="6" xs="6" className="text-start">
                        <div className="label-text">Ø / GAST</div>
                        <div className="big-text-bold">
                          {formatCurrency(profit?.perPerson?.avgProfitPerPerson ?? 0)}€
                        </div>
                        <div
                          className={`label-text ${
                            avgProPersonPercentage >= 0 ? 'text-success' : 'text-danger'
                          }`}>
                          {avgProPersonPercentage}%
                        </div>
                      </Col>
                      <Col lg="6" sm="6" md="6" xs="6" className="text-center">
                        <Row className="d-flex justify-content-between">
                          <div className="label-text">Ø / TISCH</div>
                          <div className="label-text font-weight-bold">
                            {formatCurrency(profit?.perPerson?.avgProfitPerOrder ?? 0)}€
                          </div>
                        </Row>
                        <Row className="d-flex justify-content-between my-2">
                          <div className="label-text">Ø / GAST-TISCH</div>
                          <div className="label-text font-weight-bold">2</div>
                        </Row>
                        <Row className="d-flex justify-content-between">
                          <div className="label-text">Ø / STD</div>
                          <div className="label-text font-weight-bold">
                            {formatCurrency(profit?.perHours?.value ?? 0)}€
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              {/* Card containing average statistics */}
              <Row>
                <Col xs="12" sm="12" md="6" lg="6">
                  <div className="card session-card">
                    <div className="d-flex align-items-center justify-content-between mx-3 mt-3">
                      <div className="title-text ">TIMING</div>
                      <img
                        src={icArrow}
                        alt="arrow"
                        width={25}
                        height={25}
                        style={{ objectFit: 'contain' }}
                        onClick={() => history.push('/store/kitchen/users')}
                      />
                    </div>
                    <Row className="card-body mr-2">
                      <Col lg="5" sm="5" md="5" xs="5">
                        <div className="label-text">SESSION</div>
                        <div className="big-text-bold">
                          {secondsToMinutes(timing?.perOrder?.avgSessionStartToClosed) ?? 0} MIN
                        </div>
                      </Col>
                      <Col lg="7" sm="7" md="7" xs="7">
                        <Col className="px-0 mx-0 mt-3">
                          <Row>
                            <div className="label-text medium-text-bold">PER ORDER</div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">KITCHEN</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByNonBar?.avgPreparationTimePerOrder
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">BAR</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByBar?.avgPreparationTimePerOrder
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                        </Col>

                        <Col className="px-0 mx-0 mt-3">
                          <Row>
                            <div className="label-text medium-text-bold">PER ITEM</div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">KITCHEN</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByNonBar?.avgPreparationTimePerItem
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                          <Row className="d-flex justify-content-between">
                            <div className="label-text">BAR</div>
                            <div className="medium-text-bold">
                              {secondsToMinutes(
                                timing?.preparationByBar?.avgPreparationTimePerItem
                              )}{' '}
                              MIN
                            </div>
                          </Row>
                        </Col>

                        <Row className="d-flex justify-content-between mt-3">
                          <div className="label-text">COOKING START</div>
                          <div className="medium-text-bold">
                            {secondsToMinutes(timing?.cookingStart?.avgOrderToCookingStarted)} MIN
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

export default Dashboard;
